import { Box, Stack } from '@mui/joy';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import partFinderNoProductTypes from 'assets/partFinderNoProductTypes.svg';
import { useGetPartFinderProductTypes } from 'companySettings/hooks';
import { AgGridTable, Button, PageTitle } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { Color, Size } from 'theme/enums';

import {
  DownloadPartFinderProductTypesButton,
  EditPartFinderProductTypeButton,
  PublishPartFinderProductTypesButton,
  RemovePartFinderProductTypesButton,
} from './actions';
import { getColumnDefs } from './columns';
import { UpsertPartFinderProductTypeButton } from './UpsertPartFinderProductType';
import { PartFinderEmptyState } from '../PartFinderEmptyState';

const TABLE_MIN_HEIGHT = 320;

// Narrower than the grid's default minimum, so the columns can be dragged small.
const DEFAULT_COL_DEF = { minWidth: 40 };

export const PartFinderProductTypes = () => {
  const { t } = useTranslation();

  const { records, isLoading, loadingPercentage, currentPage } = useGetPartFinderProductTypes();

  const columnDefs = useMemo(() => getColumnDefs(), []);

  const actionButtons = useCallback(
    () => [<UpsertPartFinderProductTypeButton key="addPartFinderProductTypeButton" />],
    []
  );

  // Every row update clears the selection, so the selected rows are always the latest ones.
  const selectionActionButtons = useMemo(
    () => [
      <EditPartFinderProductTypeButton key="editPartFinderProductTypeButton" />,
      <PublishPartFinderProductTypesButton key="publishPartFinderProductTypesButton" />,
      <DownloadPartFinderProductTypesButton key="downloadPartFinderProductTypesButton" />,
      <RemovePartFinderProductTypesButton key="removePartFinderProductTypesButton" />,
    ],
    []
  );

  const emptyStateElement = useMemo(
    () => (
      <PartFinderEmptyState
        image={partFinderNoProductTypes}
        message={t('messagePartFinderNoProductTypes')}
        action={
          <UpsertPartFinderProductTypeButton
            buttonElement={
              <Button
                text={t('buttonLabelAddPartFinderProductType')}
                color={Color.SUCCESS}
                size={Size.MD}
              />
            }
          />
        }
      />
    ),
    [t]
  );

  return (
    <Stack sx={{ height: '100%' }}>
      <PageTitle title={t('titlePartFinderProductTypes')} />

      {/* CompanySettingsLayout gives its child a definite height, so the grid takes what is left
          and its footer stays in view; the floor keeps it usable on short viewports. */}
      <Box sx={{ flexGrow: 1, minHeight: TABLE_MIN_HEIGHT, overflow: 'hidden' }}>
        <AgGridClientTableProvider
          tableId="partFinderProductTypes"
          entityType="ProductType"
          columnDefs={columnDefs}
          data={records}
          isDataLoading={isLoading}
          loadingPercentage={loadingPercentage}
          dataCurrentPage={currentPage}
          layoutType={TableLayoutType.PART_FINDER_PRODUCT_TYPE}
          defaultColDef={DEFAULT_COL_DEF}
          hasLayoutActions
          headerCheckbox
        >
          <AgGridTable
            actionButtons={actionButtons}
            selectionActionButtons={selectionActionButtons}
            emptyStateElement={emptyStateElement}
            hideTopActionsWhenEmpty
          />
        </AgGridClientTableProvider>
      </Box>
    </Stack>
  );
};
